/**
 * FleschAnalyzerDialog.jsx
 *
 * Flesch Reading Ease (FRE) + Flesch-Kincaid Grade Level (FKGL) analyzer.
 * Calculates readability scores for the current chapter or whole manuscript,
 * then lets the author adjust the prose to a target reading grade via AI.
 *
 * Analysis:  pure Python backend — instant, no LLM.
 * Adjustment: async background LLM job (same pattern as Arc Walker).
 *
 * NOTE: Flesch formulas are calibrated for English only.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  AlertTriangle,
  BookOpen,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  GraduationCap,
  Info,
  Loader2,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Wand2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 3000;

// ── Grade level catalogue ────────────────────────────────────────────────────
const GRADE_LEVELS = [
  { grade: 4.0,  label: 'Grade 4',             detail: 'Elementary — very short sentences, everyday words',     fre_min: 90 },
  { grade: 5.0,  label: 'Grade 5',             detail: 'Elementary — easy reading',                             fre_min: 80 },
  { grade: 6.0,  label: 'Grade 6',             detail: 'Middle school — fairly easy',                           fre_min: 70 },
  { grade: 7.0,  label: 'Grade 7',             detail: 'Middle school — easy for most teens',                   fre_min: 65 },
  { grade: 8.0,  label: 'Grade 8 ★ Standard',  detail: 'Comfortable for most adults — recommended for broad appeal', fre_min: 60 },
  { grade: 9.0,  label: 'Grade 9',             detail: 'High school — some complexity',                         fre_min: 50 },
  { grade: 10.0, label: 'Grade 10',            detail: 'High school — longer sentences, richer vocabulary',    fre_min: 45 },
  { grade: 11.0, label: 'Grade 11',            detail: 'High school senior — complex writing',                 fre_min: 38 },
  { grade: 12.0, label: 'Grade 12',            detail: 'Pre-college — sophisticated prose',                    fre_min: 30 },
  { grade: 13.0, label: 'College Year 1',      detail: 'College freshman — advanced',                          fre_min: 22 },
  { grade: 14.0, label: 'College Year 2',      detail: 'College — very difficult, dense prose',                fre_min: 15 },
  { grade: 15.0, label: 'College Year 3',      detail: 'College — dense academic writing',                     fre_min: 8  },
  { grade: 16.0, label: 'Post-Graduate',       detail: 'PhD / academic — extremely dense',                     fre_min: 0  },
];

// ── FRE colour helpers ───────────────────────────────────────────────────────
function freColor(fre) {
  if (fre == null) return 'text-muted-foreground';
  if (fre >= 60) return 'text-emerald-600';
  if (fre >= 30) return 'text-amber-600';
  return 'text-rose-600';
}

function freBg(fre) {
  if (fre == null) return 'bg-muted/40 border-border';
  if (fre >= 60) return 'bg-emerald-50 border-emerald-200';
  if (fre >= 30) return 'bg-amber-50 border-amber-200';
  return 'bg-rose-50 border-rose-200';
}

function freBarColor(fre) {
  if (fre == null) return 'bg-muted';
  if (fre >= 60) return 'bg-emerald-500';
  if (fre >= 30) return 'bg-amber-500';
  return 'bg-rose-500';
}

// ── Score card ───────────────────────────────────────────────────────────────
function ScoreCard({ label, fre, fkgl, interpretation, stats, compact = false }) {
  if (fre == null) return null;
  const pct = Math.max(0, Math.min(100, fre));

  return (
    <div className={cn('rounded-lg border p-3 space-y-2.5', freBg(fre))}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span className={cn('text-3xl font-black tabular-nums', freColor(fre))}>{fre}</span>
            <span className="text-[13px] text-muted-foreground">FRE</span>
            <span className="text-[13px] text-muted-foreground">·</span>
            <span className="text-[14px] font-semibold text-foreground">Grade {fkgl}</span>
            {stats?.language && stats.language !== 'en' && (
              <span
                className="rounded border border-amber-300/60 bg-amber-50 px-1 text-[9.5px] font-semibold uppercase text-amber-700"
                title={stats.language_note || ''}
              >
                {stats.language.toUpperCase()}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-[13.5px] text-muted-foreground leading-snug">{interpretation}</p>
          {stats?.language_note && (
            <p className="mt-1 text-[13px] text-amber-700 leading-snug">{stats.language_note}</p>
          )}
        </div>
        {!compact && stats && (
          <div className="shrink-0 space-y-0.5 text-right">
            <Stat label="Words" value={stats.n_words?.toLocaleString()} />
            <Stat label="Sentences" value={stats.n_sentences?.toLocaleString()} />
            <Stat label="ASL" value={stats.asl} />
            <Stat label="ASW" value={stats.asw} />
          </div>
        )}
      </div>
      <div className="space-y-0.5">
        <div className="flex justify-between text-[13px] text-muted-foreground">
          <span>Very Difficult (0)</span><span>Very Easy (100)</span>
        </div>
        <div className="relative h-2.5 rounded-full bg-white/60 border border-white/30 overflow-hidden">
          {/* background gradient strip */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #ef4444 0%, #f59e0b 30%, #22c55e 60%)' }} />
          {/* needle marker */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-foreground/80 shadow-sm"
            style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
          />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-[13px]">
      <span className="text-muted-foreground">{label}: </span>
      <span className="font-semibold text-foreground tabular-nums">{value}</span>
    </div>
  );
}

// ── Chapter breakdown table ───────────────────────────────────────────────────
function ChapterBreakdown({ chapters }) {
  const [open, setOpen] = useState(false);
  if (!chapters || chapters.length === 0) return null;

  return (
    <div className="rounded-md border border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13.5px] font-semibold hover:bg-muted/20 transition-colors"
      >
        <BookOpen className="h-3.5 w-3.5 text-primary" />
        <span className="flex-1">Chapter Breakdown ({chapters.length} chapters)</span>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
      {open && (
        <div className="border-t border-border">
          <ScrollArea className="h-56">
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-muted/50">
                <tr>
                  <th className="px-3 py-1.5 text-left font-semibold text-muted-foreground">#</th>
                  <th className="px-3 py-1.5 text-left font-semibold text-muted-foreground">Chapter</th>
                  <th className="px-3 py-1.5 text-center font-semibold text-muted-foreground">FRE</th>
                  <th className="px-3 py-1.5 text-center font-semibold text-muted-foreground">Grade</th>
                  <th className="px-3 py-1.5 text-right font-semibold text-muted-foreground">Words</th>
                </tr>
              </thead>
              <tbody>
                {chapters.map((ch) => (
                  <tr key={ch.chapter_id} className="border-t border-border/40 hover:bg-muted/20">
                    <td className="px-3 py-1.5 text-muted-foreground">{ch.chapter_number}</td>
                    <td className="px-3 py-1.5 max-w-[200px] truncate text-foreground">{ch.chapter_title}</td>
                    <td className={cn('px-3 py-1.5 text-center font-semibold tabular-nums', freColor(ch.fre))}>
                      {ch.fre}
                    </td>
                    <td className="px-3 py-1.5 text-center text-muted-foreground tabular-nums">
                      {ch.fkgl}
                    </td>
                    <td className="px-3 py-1.5 text-right text-muted-foreground tabular-nums">
                      {ch.n_words?.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

// ── Adjusted prose result ───────────────────────────────────────────────────
function AdjustResult({ result, onDismiss }) {
  const [copied, setCopied] = useState(false);
  if (!result) return null;

  const copy = () => {
    navigator.clipboard.writeText(result.adjusted_text || '').then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  const before = result.before || {};
  const after = result.after || {};

  return (
    <div className="space-y-3 rounded-md border border-primary/25 bg-primary/5 p-3">
      <div className="flex items-center justify-between">
        <p className="text-[13.5px] font-semibold text-primary">Adjusted prose — Ch. {result.chapter_number}: {result.chapter_title}</p>
        <Button variant="ghost" size="sm" className="h-6 text-[13px]" onClick={onDismiss}>Dismiss</Button>
      </div>
      {/* Before / After comparison */}
      <div className="grid grid-cols-2 gap-2">
        <ScoreCard label="Before" fre={before.fre} fkgl={before.fkgl} interpretation={before.interpretation} stats={before} compact />
        <ScoreCard label="After"  fre={after.fre}  fkgl={after.fkgl}  interpretation={after.interpretation}  stats={after}  compact />
      </div>
      {result.truncated && (
        <div className="flex items-center gap-1.5 rounded-sm border border-amber-200 bg-amber-50 px-2 py-1 text-[13px] text-amber-800">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          Chapter was very long — only the first 8 000 characters were adjusted. The tail was preserved as-is.
        </div>
      )}
      <div className="relative">
        <ScrollArea className="h-60 rounded-md border border-border bg-muted/10 p-3">
          <pre className="whitespace-pre-wrap font-serif text-[14px] leading-relaxed text-foreground">
            {result.adjusted_text}
          </pre>
        </ScrollArea>
        <button
          type="button"
          onClick={copy}
          className="absolute top-2 right-2 flex items-center gap-1 rounded-sm border border-border bg-background px-2 py-0.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
        >
          <Copy className="h-2.5 w-2.5" />
          {copied ? 'Copied!' : 'Copy prose'}
        </button>
      </div>
      {result.route && (
        <p className="text-[13px] text-muted-foreground text-right">via {result.route}</p>
      )}
    </div>
  );
}

// ── SVG Sparkline ─────────────────────────────────────────────────────────────
function Sparkline({ points, width = 120, height = 36, color = 'hsl(var(--primary))' }) {
  if (!points || points.length < 2) {
    // Single point — show a dot
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
        <circle cx={width / 2} cy={height / 2} r={3} fill={color} />
      </svg>
    );
  }
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const PAD = 4;
  const W = width - PAD * 2;
  const H = height - PAD * 2;
  const coords = points.map((v, i) => {
    const x = PAD + (i / (points.length - 1)) * W;
    const y = PAD + H - ((v - min) / range) * H;
    return [x, y];
  });
  const polyline = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const last = coords[coords.length - 1];

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <polyline
        points={polyline}
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.85"
      />
      {/* End dot */}
      <circle cx={last[0]} cy={last[1]} r={2.5} fill={color} />
      {/* Start dot */}
      <circle cx={coords[0][0]} cy={coords[0][1]} r={2} fill={color} opacity="0.5" />
    </svg>
  );
}

// ── History Panel ─────────────────────────────────────────────────────────────
function HistoryPanel({ history, loading, onRefresh }) {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground" data-testid="flesch-history-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-[13.5px]">Loading history…</p>
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground" data-testid="flesch-history-empty">
        <Clock className="h-8 w-8 opacity-30" />
        <p className="text-[13px] font-medium">No rewrite history yet</p>
        <p className="text-[13px] text-center max-w-xs">
          Run the Flesch adjuster on a chapter or the whole manuscript and your
          before/after scores will appear here.
        </p>
        <button
          onClick={onRefresh}
          className="mt-1 flex items-center gap-1.5 rounded-sm border px-3 py-1 text-[13px] hover:bg-secondary transition-colors"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>
    );
  }

  // ── Group by chapter ──────────────────────────────────────────────────────
  const byChapter = {};
  [...history].reverse().forEach((entry) => {         // oldest → newest
    const cid = entry.chapter_id || 'unknown';
    if (!byChapter[cid]) {
      byChapter[cid] = {
        title: entry.chapter_title || 'Untitled',
        number: entry.chapter_number,
        entries: [],
      };
    }
    byChapter[cid].entries.push(entry);
  });

  return (
    <div className="space-y-5" data-testid="flesch-history-list">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
          {history.length} rewrite {history.length === 1 ? 'pass' : 'passes'} ·{' '}
          {Object.keys(byChapter).length}{' '}
          {Object.keys(byChapter).length === 1 ? 'chapter' : 'chapters'}
        </p>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
          data-testid="flesch-history-refresh"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Per-chapter sparkline + detail cards */}
      {Object.entries(byChapter).map(([cid, { title, number, entries }]) => {
        // Build FRE timeline: before of first entry, then all afters
        const allFRE = [
          entries[0]?.before_fre,
          ...entries.map((e) => e.after_fre),
        ].filter((v) => v != null);

        const latest = entries[entries.length - 1];
        const totalDelta = allFRE.length >= 2
          ? (allFRE[allFRE.length - 1] - allFRE[0]).toFixed(1)
          : null;
        const improved = totalDelta !== null && parseFloat(totalDelta) > 0;
        const worsened = totalDelta !== null && parseFloat(totalDelta) < 0;
        const sparkColor = improved
          ? 'hsl(142 71% 45%)'
          : worsened
            ? 'hsl(4 86% 58%)'
            : 'hsl(var(--primary))';

        return (
          <div key={cid} className="rounded-xl border border-border bg-muted/10 overflow-hidden">
            {/* Chapter header with sparkline */}
            <div className="flex items-center gap-3 px-3 py-2.5 border-b border-border/50 bg-muted/5">
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-semibold text-foreground truncate">
                  {title}
                  {number != null && (
                    <span className="ml-1.5 text-[13px] font-normal text-muted-foreground">
                      Ch.{number}
                    </span>
                  )}
                </p>
                <p className="text-[13px] text-muted-foreground mt-0.5">
                  {entries.length} {entries.length === 1 ? 'pass' : 'passes'} · Target grade {latest?.target_grade}
                </p>
              </div>

              {/* Sparkline */}
              {allFRE.length >= 2 && (
                <div className="shrink-0 flex items-center gap-2">
                  <Sparkline points={allFRE} width={96} height={30} color={sparkColor} />
                  {totalDelta !== null && (
                    <div className={cn('flex items-center gap-0.5 text-[13px] font-bold tabular-nums',
                      improved ? 'text-emerald-600' : worsened ? 'text-rose-500' : 'text-muted-foreground'
                    )}>
                      {improved ? <TrendingUp className="h-3 w-3" /> : worsened ? <TrendingDown className="h-3 w-3" /> : null}
                      {improved ? '+' : ''}{totalDelta}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Entry rows */}
            <div className="divide-y divide-border/40">
              {[...entries].reverse().map((entry, idx) => {
                const delta = entry.after_fre != null && entry.before_fre != null
                  ? (entry.after_fre - entry.before_fre).toFixed(1) : null;
                const imp = delta !== null && parseFloat(delta) > 0;
                const wor = delta !== null && parseFloat(delta) < 0;
                const date = entry.rewritten_at
                  ? new Date(entry.rewritten_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                  : '—';

                return (
                  <div
                    key={idx}
                    className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2 px-3 py-2"
                    data-testid={`flesch-history-entry-${idx}`}
                  >
                    {/* Before */}
                    <div className={cn('rounded px-2 py-1 text-center text-[13px]', freBg(entry.before_fre))}>
                      <span className={cn('font-bold tabular-nums', freColor(entry.before_fre))}>
                        {entry.before_fre ?? '—'}
                      </span>
                      <span className="ml-1 text-muted-foreground text-[9px]">FRE</span>
                    </div>

                    {/* Arrow + delta */}
                    <div className="flex flex-col items-center text-[13px]">
                      {imp
                        ? <TrendingUp className="h-3 w-3 text-emerald-600" />
                        : wor
                          ? <TrendingDown className="h-3 w-3 text-rose-500" />
                          : <span className="text-muted-foreground">—</span>
                      }
                      {delta !== null && (
                        <span className={cn('tabular-nums font-semibold',
                          imp ? 'text-emerald-600' : wor ? 'text-rose-500' : 'text-muted-foreground'
                        )}>
                          {imp ? '+' : ''}{delta}
                        </span>
                      )}
                    </div>

                    {/* After */}
                    <div className={cn('rounded px-2 py-1 text-center text-[13px]', freBg(entry.after_fre))}>
                      <span className={cn('font-bold tabular-nums', freColor(entry.after_fre))}>
                        {entry.after_fre ?? '—'}
                      </span>
                      <span className="ml-1 text-muted-foreground text-[9px]">FRE</span>
                    </div>

                    {/* Date */}
                    <div className="text-[9.5px] text-muted-foreground text-right whitespace-nowrap">
                      {date}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function FleschAnalyzerDialog({
  open,
  onClose,
  projectId,
  activeChapterId,
  chapters = [],
}) {
  const [analysisScope, setAnalysisScope] = useState('chapter');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);

  // History state
  const [historyData, setHistoryData]     = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Adjustment state
  const [targetGrade, setTargetGrade] = useState('8.0');
  const [adjusting, setAdjusting] = useState(false);
  const [adjustJobId, setAdjustJobId] = useState(null);
  const [adjustResult, setAdjustResult] = useState(null);
  const [adjustError, setAdjustError] = useState(null);
  const adjustPollRef = useRef(null);

  // Batch (whole-novel) adjustment state
  const [batchAdjusting, setBatchAdjusting] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null); // {done, total, current_chapter}
  const [batchResults, setBatchResults] = useState(null);   // chapter_results array
  const [batchError, setBatchError] = useState(null);
  const batchPollRef = useRef(null);

  // Accept-all state (commit batch rewrites to DB)
  const [acceptingAll, setAcceptingAll] = useState(false);
  const [acceptAllProgress, setAcceptAllProgress] = useState(null); // {done, total}
  const [acceptAllDone, setAcceptAllDone] = useState(false);

  // Per-chapter preview + individual accept state
  const [expandedChapter, setExpandedChapter] = useState(null);   // chapter_id | null
  const [acceptedChapters, setAcceptedChapters] = useState(new Set()); // Set<chapter_id>
  const [acceptingChapter, setAcceptingChapter] = useState(null); // chapter_id | null
  const [copiedChapterId, setCopiedChapterId] = useState(null);   // chapter_id | null

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  const currentChapter = chapters.find((c) => c.id === activeChapterId) || chapters[0];
  const selectedGradeObj = GRADE_LEVELS.find((g) => String(g.grade) === targetGrade) || GRADE_LEVELS[4];

  // Stop adjust polling on unmount
  useEffect(() => () => {
    if (adjustPollRef.current)  clearInterval(adjustPollRef.current);
    if (batchPollRef.current)   clearInterval(batchPollRef.current);
  }, []);

  const startAdjustPoll = useCallback((jobId) => {
    if (adjustPollRef.current) clearInterval(adjustPollRef.current);
    adjustPollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/projects/${projectId}/readability/adjust/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        if (data.status === 'done' && data.result) {
          clearInterval(adjustPollRef.current); adjustPollRef.current = null;
          setAdjusting(false);
          setAdjustResult(data.result);
          toast.success('Prose adjusted — review and copy below');
        } else if (data.status === 'failed') {
          clearInterval(adjustPollRef.current); adjustPollRef.current = null;
          setAdjusting(false);
          setAdjustError(data.error || 'Adjustment failed');
          toast.error(data.error || 'Prose adjustment failed');
        }
      } catch { /* transient */ }
    }, POLL_MS);
  }, [projectId, token]);

  const runAnalysis = async () => {
    if (!projectId || analyzing) return;
    setAnalyzing(true);
    setAnalysisResult(null);
    setAnalysisError(null);
    try {
      const scope = analysisScope;
      const params = new URLSearchParams({ scope });
      if (scope === 'chapter' && currentChapter?.id) {
        params.set('chapter_id', currentChapter.id);
      }
      const r = await fetch(`${API}/api/projects/${projectId}/readability/analyze?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Analysis failed');
      }
      const data = await r.json();
      setAnalysisResult(data);
    } catch (e) {
      setAnalysisError(e.message);
      toast.error(e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  // ── Fetch rewrite history ──────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    if (!projectId) return;
    setHistoryLoading(true);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/readability/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed to load history');
      const data = await r.json();
      setHistoryData(data.history || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load readability history');
    } finally {
      setHistoryLoading(false);
    }
  }, [projectId, token]);

  // Fetch history when user opens the History tab
  useEffect(() => {
    if (analysisScope === 'history' && historyData === null) {
      fetchHistory();
    }
  }, [analysisScope, historyData, fetchHistory]);

  const runAdjust = async () => {
    if (!projectId || adjusting) return;
    if (!currentChapter?.id) {
      toast.error('Open a chapter first'); return;
    }
    setAdjusting(true);
    setAdjustError(null);
    setAdjustResult(null);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/readability/adjust`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ chapter_id: currentChapter.id, target_grade: parseFloat(targetGrade) }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to start adjustment');
      }
      const { job_id } = await r.json();
      setAdjustJobId(job_id);
      startAdjustPoll(job_id);
    } catch (e) {
      setAdjusting(false);
      setAdjustError(e.message);
      toast.error(e.message);
    }
  };

  // ── Whole-novel (batch) adjustment ──────────────────────────────────────────
  const startBatchPoll = useCallback((bid) => {
    if (batchPollRef.current) clearInterval(batchPollRef.current);
    batchPollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/projects/${projectId}/readability/adjust-all/${bid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        if (data.progress) setBatchProgress(data.progress);
        if (data.status === 'done') {
          clearInterval(batchPollRef.current); batchPollRef.current = null;
          setBatchAdjusting(false);
          setBatchResults(data.chapter_results || []);
          toast.success(`Novel rewritten — ${data.chapter_results?.length ?? 0} chapters adjusted`);
        } else if (data.status === 'failed') {
          clearInterval(batchPollRef.current); batchPollRef.current = null;
          setBatchAdjusting(false);
          setBatchError(data.error || 'Batch adjustment failed');
          toast.error(data.error || 'Batch adjustment failed');
        }
      } catch { /* transient */ }
    }, POLL_MS);
  }, [projectId, token]);

  const runAdjustAll = async () => {
    if (!projectId || batchAdjusting) return;
    setBatchAdjusting(true);
    setBatchError(null);
    setBatchResults(null);
    setBatchProgress(null);
    setAcceptAllDone(false);
    setAcceptAllProgress(null);
    setAcceptedChapters(new Set());
    setExpandedChapter(null);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/readability/adjust-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ target_grade: parseFloat(targetGrade) }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to start batch adjustment');
      }
      const data = await r.json();
      setBatchId(data.batch_id);
      setBatchProgress({ done: 0, total: data.total_chapters, current_chapter: null });
      startBatchPoll(data.batch_id);
      toast.info(`Rewriting ${data.total_chapters} chapters to Grade ${targetGrade}…`);
    } catch (e) {
      setBatchAdjusting(false);
      setBatchError(e.message);
      toast.error(e.message);
    }
  };

  // ── Accept all batch rewrites — commit to DB ────────────────────────────
  const runAcceptAll = async () => {
    if (!batchResults || acceptingAll) return;
    const toAccept = batchResults.filter(
      (r) => r.status === 'done' && r.result?.adjusted_text && !acceptedChapters.has(r.chapter_id)
    );
    if (toAccept.length === 0) {
      toast.info('No successful rewrites to apply.');
      return;
    }
    setAcceptingAll(true);
    setAcceptAllProgress({ done: 0, total: toAccept.length });
    setAcceptAllDone(false);
    let done = 0;
    for (const cr of toAccept) {
      try {
        await fetch(`${API}/api/chapters/${cr.chapter_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ content: cr.result.adjusted_text }),
        });
      } catch { /* continue even if one chapter fails */ }
      done += 1;
      setAcceptAllProgress({ done, total: toAccept.length });
    }
    setAcceptingAll(false);
    setAcceptAllDone(true);
    toast.success(
      `${done} chapter${done !== 1 ? 's' : ''} saved to your manuscript.`
    );
  };

  // ── Accept one chapter — commit individual rewrite to DB ─────────────────
  const acceptOneChapter = async (chapterId, adjustedText) => {
    if (acceptingChapter) return;
    setAcceptingChapter(chapterId);
    try {
      const r = await fetch(`${API}/api/chapters/${chapterId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: adjustedText }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save chapter');
      }
      setAcceptedChapters((prev) => new Set([...prev, chapterId]));
      setExpandedChapter(null);
      toast.success('Chapter saved to manuscript.');
    } catch (e) {
      toast.error(e.message || 'Failed to save chapter. Try again.');
    } finally {
      setAcceptingChapter(null);
    }
  };

  // ── Helper to reset all batch + accept state ─────────────────────────────
  const clearBatchState = () => {
    setBatchResults(null);
    setAcceptAllDone(false);
    setAcceptAllProgress(null);
    setAcceptedChapters(new Set());
    setExpandedChapter(null);
    setCopiedChapterId(null);
  };

  // ── Copy a single chapter's rewritten prose to clipboard ─────────────────
  const copyChapterText = (chapterId, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedChapterId(chapterId);
      setTimeout(() => setCopiedChapterId((id) => (id === chapterId ? null : id)), 2000);
    });
  };

  // Auto-analyze on open
  useEffect(() => {
    if (open && !analysisResult && !analyzing) {
      runAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const showAnalysisFor = analysisResult;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="flex flex-col p-0 overflow-hidden"
        style={{ width: 'min(860px, 94vw)', maxWidth: 'min(860px, 94vw)', height: '90dvh', maxHeight: '90dvh' }}
        data-testid="flesch-analyzer-dialog"
      >
        {/* Header */}
        <DialogHeader className="shrink-0 border-b border-border bg-panel-header/40 px-5 py-3">
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-2 text-[14px]">
              <GraduationCap className="h-4 w-4 text-primary" />
              Flesch Reading Ease Analyzer
            </DialogTitle>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setAnalysisScope('chapter')}
                  className={cn('rounded-sm border px-2.5 py-0.5 text-[13px] font-medium transition-colors',
                    analysisScope === 'chapter' ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:text-foreground'
                  )}
                >
                  Chapter
                </button>
                <button
                  type="button"
                  onClick={() => setAnalysisScope('all')}
                  className={cn('rounded-sm border px-2.5 py-0.5 text-[13px] font-medium transition-colors',
                    analysisScope === 'all' ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:text-foreground'
                  )}
                >
                  Manuscript
                </button>
                <button
                  type="button"
                  onClick={() => { setAnalysisScope('history'); setHistoryData(null); }}
                  className={cn('flex items-center gap-1 rounded-sm border px-2.5 py-0.5 text-[13px] font-medium transition-colors',
                    analysisScope === 'history' ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:text-foreground'
                  )}
                  data-testid="flesch-history-tab"
                >
                  <Clock className="h-3 w-3" />
                  History
                </button>
              </div>
              {analysisScope !== 'history' && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1.5 text-[13px]"
                onClick={runAnalysis}
                disabled={analyzing}
                data-testid="flesch-run-analysis-btn"
              >
                {analyzing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                {analyzing ? 'Analyzing…' : 'Analyze'}
              </Button>
              )}
            </div>
          </div>
          {/* Sticky grade selector — always visible even when scrolling through results */}
          {analysisScope !== 'history' && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border/40 pt-2" data-testid="flesch-sticky-grade-row">
              <label className="text-[13px] font-semibold text-muted-foreground shrink-0">
                Target grade:
              </label>
              <Select value={targetGrade} onValueChange={setTargetGrade}>
                <SelectTrigger className="h-8 w-52 text-[13px]" data-testid="flesch-grade-select">
                  <SelectValue>
                    {GRADE_LEVELS.find((g) => g.grade.toFixed(1) === targetGrade)?.label}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {GRADE_LEVELS.map((g) => (
                    <SelectItem key={g.grade} value={g.grade.toFixed(1)} data-testid={`flesch-grade-${g.grade}`}>
                      {g.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[13px] text-muted-foreground">{selectedGradeObj?.detail}</span>
              {showAnalysisFor?.fre != null && (
                <span className="ml-auto flex items-center gap-2 text-[13px] tabular-nums shrink-0">
                  <span className={cn('font-semibold', freColor(showAnalysisFor.fre))}>
                    Current: {showAnalysisFor.fre} FRE
                  </span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-semibold text-primary">
                    Target: ~{selectedGradeObj?.fre_min} FRE
                  </span>
                </span>
              )}
            </div>
          )}

          {/* English-only note */}
          <div className="mt-1.5 flex items-center gap-1.5 rounded-sm border border-sky-200/60 bg-sky-50/70 px-2.5 py-1 text-[13px] text-sky-800">
            <Info className="h-3 w-3 shrink-0" />
            <span>
              <strong>English only</strong> — Flesch formulas are calibrated for English.
              Scores for other languages may not be accurate.
            </span>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="px-5 py-4 space-y-5">

            {/* ── Step-by-step instructions ──────────────────────────── */}
            <div
              className="grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-md border border-sky-200/60 bg-sky-50/50 p-3"
              data-testid="flesch-steps-guide"
            >
              {[
                { n: 1, title: 'Analyze',     desc: 'Click "Analyze" — instant scoring, no AI used.' },
                { n: 2, title: 'Set target',  desc: 'Pick a reading grade in the sticky bar above.' },
                { n: 3, title: 'Adjust',      desc: 'Click "Adjust to Grade X" — AI rewrites the active chapter, preserving story events.' },
                { n: 4, title: 'Accept',      desc: 'Review the before/after comparison, then Accept to save to your manuscript.' },
              ].map(({ n, title, desc }) => (
                <div key={n} className="flex gap-2">
                  <span className="flex-none flex items-center justify-center h-5 w-5 rounded-full bg-sky-500 text-white text-[11px] font-bold mt-0.5 shrink-0">
                    {n}
                  </span>
                  <div>
                    <p className="text-[13px] font-semibold text-sky-800 leading-tight">{title}</p>
                    <p className="text-[13px] text-sky-700 leading-snug mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ── History panel ─────────────────────────────────────────── */}
            {analysisScope === 'history' && (
              <HistoryPanel
                history={historyData}
                loading={historyLoading}
                onRefresh={fetchHistory}
              />
            )}

            {/* ── Normal analysis content ────────────────────────────────── */}
            {analysisScope !== 'history' && <>
            {/* Reference guide */}
            <div className="grid grid-cols-2 gap-3">
              {/* FRE guide */}
              <div className="rounded-md border border-border bg-muted/20 p-3 space-y-1.5">
                <p className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Flesch Reading Ease (FRE)
                </p>
                <p className="text-[13px] text-muted-foreground font-mono">
                  206.835 − (1.015 × ASL) − (84.6 × ASW)
                </p>
                <div className="space-y-0.5 text-[13px]">
                  <div className="flex gap-2"><span className="font-mono font-semibold text-emerald-700 w-12">90–100</span><span className="text-muted-foreground">Very Easy (5th grade)</span></div>
                  <div className="flex gap-2"><span className="font-mono font-semibold text-emerald-600 w-12">60–70</span><span className="text-muted-foreground">Standard — recommended range</span></div>
                  <div className="flex gap-2"><span className="font-mono font-semibold text-rose-600  w-12">0–30</span><span className="text-muted-foreground">Very Difficult (college+)</span></div>
                </div>
              </div>
              {/* FKGL guide */}
              <div className="rounded-md border border-border bg-muted/20 p-3 space-y-1.5">
                <p className="text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Flesch-Kincaid Grade Level (FKGL)
                </p>
                <p className="text-[13px] text-muted-foreground font-mono">
                  (0.39 × ASL) + (11.8 × ASW) − 15.59
                </p>
                <div className="space-y-0.5 text-[13px]">
                  <div className="flex gap-2"><span className="font-mono font-semibold text-foreground w-4">8</span><span className="text-muted-foreground">Standard documents (recommended)</span></div>
                  <div className="flex gap-2"><span className="font-mono font-semibold text-foreground w-4">5</span><span className="text-muted-foreground">Mass-market fiction / news</span></div>
                  <div className="flex gap-2"><span className="font-mono font-semibold text-foreground w-4">14</span><span className="text-muted-foreground">Academic papers</span></div>
                </div>
                <p className="text-[13px] text-muted-foreground italic">Measures U.S. school grade required to read comfortably.</p>
              </div>
            </div>

            {/* Loading */}
            {analyzing && (
              <div className="flex flex-col items-center gap-3 py-8 text-muted-foreground">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
                <p className="text-sm">Counting words, sentences and syllables…</p>
              </div>
            )}

            {/* Error */}
            {!analyzing && analysisError && (
              <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-[13.5px] text-rose-700">
                <p className="font-medium">Analysis failed</p>
                <p className="mt-0.5 opacity-80">{analysisError}</p>
              </div>
            )}

            {/* Results */}
            {showAnalysisFor && !analyzing && (
              <>
                <ScoreCard
                  label={showAnalysisFor.scope === 'chapter'
                    ? `Ch. ${showAnalysisFor.chapter_number} — ${showAnalysisFor.chapter_title}`
                    : `Whole manuscript (${showAnalysisFor.chapters_analyzed} chapters)`}
                  fre={showAnalysisFor.fre}
                  fkgl={showAnalysisFor.fkgl}
                  interpretation={showAnalysisFor.interpretation}
                  stats={showAnalysisFor}
                />
                {showAnalysisFor.chapter_breakdown && (
                  <ChapterBreakdown chapters={showAnalysisFor.chapter_breakdown} />
                )}
              </>
            )}

            <Separator />

            {/* ── Adjustment section ─────────────────────────────────────── */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-primary" />
                <h3 className="text-[13px] font-semibold">Adjust prose to target grade</h3>
              </div>

              {!currentChapter && (
                <p className="text-[13.5px] text-muted-foreground">Open a chapter to enable prose adjustment.</p>
              )}

              {currentChapter && (
                <>
                  <div className="rounded-md border border-border bg-muted/20 px-3 py-2 text-[13.5px] text-muted-foreground">
                    Will adjust: <strong className="text-foreground">Ch. {currentChapter.number} — {currentChapter.title}</strong>
                    <span className="ml-2 text-[13px]">→ Grade {targetGrade}</span>
                  </div>

                  <div className="flex items-center gap-4">
                    {/* Visual guide bar for target */}
                    <div className="flex-1 space-y-1">
                      <div className="flex justify-between text-[9.5px] text-muted-foreground">
                        <span>FRE 0</span>
                        <span>100</span>
                      </div>
                      <div className="relative h-2 rounded-full overflow-hidden" style={{ background: 'linear-gradient(to right, #ef4444 0%, #f59e0b 30%, #22c55e 60%)' }}>
                        {/* current score needle */}
                        {showAnalysisFor?.fre != null && (
                          <div
                            className="absolute top-0 bottom-0 w-0.5 bg-foreground/60"
                            style={{ left: `${Math.max(0, Math.min(100, showAnalysisFor.fre))}%` }}
                            title={`Current: ${showAnalysisFor.fre}`}
                          />
                        )}
                        {/* target needle */}
                        <div
                          className="absolute top-0 bottom-0 w-1 bg-primary/90 rounded-full"
                          style={{ left: `${Math.max(0, Math.min(100, selectedGradeObj?.fre_min ?? 65))}%` }}
                          title={`Target: ~${selectedGradeObj?.fre_min}`}
                        />
                      </div>
                      <div className="flex justify-between text-[9.5px]">
                        <span className="text-muted-foreground">Current: <strong>{showAnalysisFor?.fre ?? '—'}</strong></span>
                        <span className="text-primary">Target: <strong>~{selectedGradeObj?.fre_min}</strong></span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={runAdjust}
                    disabled={adjusting || !showAnalysisFor}
                    className="gap-2"
                    data-testid="flesch-adjust-btn"
                  >
                    {adjusting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
                    {adjusting ? 'Adjusting prose…' : `Adjust to Grade ${targetGrade}`}
                  </Button>
                  {!showAnalysisFor && (
                    <p className="text-[13px] text-muted-foreground">Run analysis first to enable adjustment.</p>
                  )}
                  {adjustError && (
                    <p className="text-[13.5px] text-rose-600">{adjustError}</p>
                  )}
                </>
              )}
            </div>

            {/* Adjustment result */}
            {adjustResult && (
              <AdjustResult result={adjustResult} onDismiss={() => setAdjustResult(null)} />
            )}

            {/* Adjustment progress */}
            {adjusting && (
              <div className="flex items-center gap-2.5 rounded-md border border-primary/20 bg-primary/5 p-3 text-[13.5px]">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <div>
                  <p className="font-medium">Rewriting prose…</p>
                  <p className="text-[13px] text-muted-foreground">The AI is adjusting sentence structure and vocabulary to match Grade {targetGrade}. Results appear automatically.</p>
                </div>
              </div>
            )}

            {/* ── Whole-novel (batch) adjustment section ────────────────── */}
            <Separator />
            <div className="space-y-3" data-testid="flesch-batch-section">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-primary" />
                <h3 className="text-[13px] font-semibold">Rewrite full novel to target grade</h3>
              </div>
              <p className="text-[13.5px] text-muted-foreground">
                Rewrites <strong>every chapter</strong> in this project to match Grade {targetGrade}.
                Each chapter is processed in sequence — prose is rewritten while story events, character names, and dialogue beats are preserved.
              </p>

              <Button
                onClick={runAdjustAll}
                disabled={batchAdjusting || adjusting}
                variant="outline"
                className="gap-2 border-primary/40 text-primary hover:bg-primary/5"
                data-testid="flesch-adjust-all-btn"
              >
                {batchAdjusting
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <BookOpen className="h-3.5 w-3.5" />
                }
                {batchAdjusting
                  ? `Rewriting… ${batchProgress?.done ?? 0} / ${batchProgress?.total ?? '?'} chapters`
                  : `Rewrite all chapters to Grade ${targetGrade}`
                }
              </Button>

              {/* Progress bar */}
              {batchAdjusting && batchProgress && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[13px] text-muted-foreground">
                    <span>
                      {batchProgress.current_chapter
                        ? <>Adjusting <strong className="text-foreground">{batchProgress.current_chapter}</strong>…</>
                        : 'Preparing…'
                      }
                    </span>
                    <span>{batchProgress.done} of {batchProgress.total}</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-500"
                      style={{ width: `${batchProgress.total > 0 ? (batchProgress.done / batchProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Batch error */}
              {batchError && (
                <p className="text-[13.5px] text-rose-600">{batchError}</p>
              )}

              {/* Batch results summary */}
              {batchResults && batchResults.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[13.5px] font-semibold text-emerald-700">
                    {batchResults.filter(r => r.status === 'done').length} of {batchResults.length} chapters rewritten successfully.
                    {acceptedChapters.size > 0 && (
                      <span className="ml-1.5 font-normal text-emerald-600">
                        ({acceptedChapters.size} saved)
                      </span>
                    )}
                  </p>
                  <div className="rounded-md border border-border divide-y divide-border text-[13.5px]">
                    {batchResults.map((cr) => {
                      const isExpanded = expandedChapter === cr.chapter_id;
                      const isAccepted = acceptedChapters.has(cr.chapter_id);
                      const isSaving = acceptingChapter === cr.chapter_id;
                      return (
                        <div key={cr.chapter_id}>
                          {/* Row header */}
                          <div className="flex items-center gap-2 px-3 py-1.5">
                            <span className="flex-1 min-w-0 truncate text-foreground">
                              Ch. {cr.chapter_number} — {cr.chapter_title}
                            </span>
                            {cr.status === 'done' ? (
                              <>
                                <span className="text-emerald-600 font-medium tabular-nums shrink-0">
                                  FKGL {cr.result?.after?.fkgl ?? '?'}
                                </span>
                                {isAccepted ? (
                                  <span className="flex items-center gap-0.5 text-emerald-600 text-[13px] font-medium shrink-0">
                                    <Check className="h-3 w-3" />
                                    Saved
                                  </span>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => setExpandedChapter(isExpanded ? null : cr.chapter_id)}
                                      className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[13px] text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
                                      data-testid={`flesch-preview-btn-${cr.chapter_id}`}
                                    >
                                      {isExpanded ? 'Hide' : 'Preview'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => acceptOneChapter(cr.chapter_id, cr.result?.adjusted_text)}
                                      disabled={isSaving || !cr.result?.adjusted_text || !!acceptingChapter}
                                      className="shrink-0 flex items-center gap-0.5 rounded border border-emerald-200 px-1.5 py-0.5 text-[13px] font-medium text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-40"
                                      data-testid={`flesch-accept-one-btn-${cr.chapter_id}`}
                                    >
                                      {isSaving
                                        ? <Loader2 className="h-3 w-3 animate-spin" />
                                        : <Check className="h-3 w-3" />}
                                      {isSaving ? '…' : 'Accept'}
                                    </button>
                                  </>
                                )}
                              </>
                            ) : (
                              <span className="text-rose-500 shrink-0">failed</span>
                            )}
                          </div>

                          {/* Expandable prose preview */}
                          {isExpanded && cr.result?.adjusted_text && (
                            <div className="border-t border-border/30 bg-muted/10 px-3 py-2.5 space-y-2">
                              <div className="relative">
                                <ScrollArea className="h-40 rounded border border-border bg-background p-2.5">
                                  <pre className="whitespace-pre-wrap font-serif text-[13.5px] leading-relaxed text-foreground">
                                    {cr.result.adjusted_text}
                                  </pre>
                                </ScrollArea>
                                {/* One-click copy button */}
                                <button
                                  type="button"
                                  onClick={() => copyChapterText(cr.chapter_id, cr.result.adjusted_text)}
                                  className="absolute right-2 top-2 flex items-center gap-1 rounded-sm border border-border bg-background px-1.5 py-0.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                                  data-testid={`flesch-copy-chapter-${cr.chapter_id}`}
                                  title="Copy rewritten prose to clipboard"
                                >
                                  {copiedChapterId === cr.chapter_id
                                    ? <><Check className="h-2.5 w-2.5 text-emerald-600" /> Copied</>
                                    : <><Copy className="h-2.5 w-2.5" /> Copy</>
                                  }
                                </button>
                              </div>
                              <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                                <span>Before: Grade {cr.result?.before?.fkgl ?? '?'} · FRE {cr.result?.before?.fre ?? '?'}</span>
                                <span>→</span>
                                <span className="font-medium text-emerald-700">
                                  After: Grade {cr.result?.after?.fkgl ?? '?'} · FRE {cr.result?.after?.fre ?? '?'}
                                </span>
                                {cr.result?.after?.language_note && (
                                  <span className="text-[13px] text-amber-700" title={cr.result.after.language_note}>
                                    ({cr.result.after.language?.toUpperCase()})
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Accept All / progress / done */}
                  {acceptAllDone ? (
                    <div
                      className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13.5px] text-emerald-800"
                      data-testid="flesch-accept-all-done"
                    >
                      <CheckCheck className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                      <span className="flex-1">All rewrites saved to your manuscript.</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-[13px] text-muted-foreground"
                        onClick={clearBatchState}
                      >
                        Dismiss
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Only show Accept All if there are unaccepted done chapters */}
                      {batchResults.filter(r => r.status === 'done' && !acceptedChapters.has(r.chapter_id)).length > 0 && (
                        <Button
                          size="sm"
                          onClick={runAcceptAll}
                          disabled={acceptingAll}
                          className="gap-1.5 h-7 text-[13.5px] bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid="flesch-accept-all-btn"
                        >
                          {acceptingAll
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <CheckCheck className="h-3 w-3" />}
                          {acceptingAll
                            ? `Saving ${acceptAllProgress?.done ?? 0} / ${acceptAllProgress?.total ?? '?'}…`
                            : `Accept all (${batchResults.filter(r => r.status === 'done' && !acceptedChapters.has(r.chapter_id)).length} remaining)`}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-[13px] text-muted-foreground"
                        onClick={clearBatchState}
                      >
                        Dismiss results
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Key limitations note */}
            <div className="rounded-md border border-border bg-muted/15 p-3 space-y-1">
              <p className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider">Key limitations</p>
              <ul className="space-y-0.5 text-[13px] text-muted-foreground">
                <li>• Flesch scores measure <em>sentence length</em> and <em>syllable count</em> only — not quality, style, or logic.</li>
                <li>• Formulas are calibrated for <strong>English</strong>. Scores for other languages will be inaccurate.</li>
                <li>• Prose adjustment preserves all story events but may alter the author&apos;s voice. Always review before applying.</li>
              </ul>
            </div>
            </> /* end analysisScope !== 'history' */}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
